import { AsyncPipe, NgIf, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { InsightService } from '@sc/data-access-http-services';
import {
  CarPart,
  Document,
  Dossier,
  Fitter,
  withPreviewFileTypes,
  PicturesViewerData,
} from '@sc/data-access-models';
import {
  AppointmentsSandboxService,
  DossierSandboxService,
  FittersSandboxService,
  SandboxService,
} from '@sc/data-access-state-management';
import { TranslatorService } from '@sc/data-access-translation';
import { AdaptableOverlayService } from '@sc/feature-adaptable-overlay';
import { AuthService } from '@sc/feature-auth-okta';
import { BrowserCallData } from '@sc/feature-browser-call';
import { DialogService } from '@sc/feature-dialog';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { LoadingService } from '@sc/feature-loader';
import { PicturesViewerService } from '@sc/feature-pictures-viewer';
import { SnackbarService } from '@sc/feature-snackbar';
import {
  ClaimPicturesComponent,
  SinisterTypeComponent,
} from '@sc/ui-components';
import { DeclarationConditions } from '@sc/utils';
import { EMPTY } from 'rxjs';
import { filter, switchMap, take, tap } from 'rxjs/operators';
import { InsurerDeclarationSteps } from '../../../../models';
import { DeclarationEndHistoryComponent } from './declaration-end-history/declaration-end-history.component';
import { DeclarationEndInformationComponent } from './declaration-end-informations/declaration-end-information.component';
import { DeclarationEndInterventionComponent } from './declaration-end-intervention/declaration-end-intervention.component';

@Component({
  selector: 'sc-declaration-end',
  styleUrls: ['./declaration-end.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DeclarationEndInformationComponent,
    DeclarationEndHistoryComponent,
    DeclarationEndInterventionComponent,
    NgIf,
    SinisterTypeComponent,
    ClaimPicturesComponent,
    AsyncPipe,
    UpperCasePipe,
    TranslateModule,
  ],
  template: `
    <h1 class="sc-design-title-h1">
      {{
        'DeclarationEnd.interventionTitle'
          | translate
            : {
                dossier: (dossier$ | async).id | uppercase,
                licensePlate: getLicensePlateValue()
              }
      }}
    </h1>

    <section class="_details">
      <section class="_left">
        <sc-declaration-end-information
          [dossier]="dossier$ | async"
          [canValidateRequest]="isFitterB2B"
          (notifyCustomerEvent)="notifyCustomer()"
          (validateRequestEvent)="validateRequest()"
        ></sc-declaration-end-information>

        <sc-declaration-end-history
          [dossier]="dossier$ | async"
        ></sc-declaration-end-history>
      </section>

      <section class="_right">
        <div class="summary-element intervention-summary">
          <sc-declaration-end-intervention
            [dossier]="dossier$ | async"
            [fitter]="fitter"
            (changeAppointment)="onChangeAppointment()"
            (cancelAppointment)="onCancelAppointment()"
          ></sc-declaration-end-intervention>
        </div>

        <div class="summary-element sinster-summary">
          <sc-sinister-type
            *ngIf="
              !!(dossier$ | async)?.claim?.car?.licensePlateNumber ||
              !!(dossier$ | async)?.claim?.glass?.glass
            "
            [dossier]="dossier$ | async"
            [showGreenBox]="false"
            [mainTitle]="'DeclarationEnd.sinisterTypeTitle' | translate"
          ></sc-sinister-type>
        </div>

        <div class="summary-element pictures-summary" *ngIf="pictures?.length">
          <h2 class="sc-design-title-h1">
            {{ 'DeclarationEnd.sinisterPhotoTitle' | translate }}
          </h2>
          <sc-claim-pictures
            [dossier]="currentDossier"
            (showPictures)="onShowPictures($event)"
            [readOnly]="true"
          ></sc-claim-pictures>
        </div>
      </section>
    </section>
  `,
})
export class DeclarationEndComponent implements OnInit, OnDestroy {
  dossier$ = this.dossierSB.dossier$;
  fitter: Fitter;
  currentDossier: Dossier;
  pictures: Document[];
  carParts: CarPart[];
  loadingCarParts = false;
  callNumbers: BrowserCallData;

  isFitterB2B = false;

  constructor(
    private readonly dossierSB: DossierSandboxService,
    private readonly sandbox: SandboxService,
    private readonly fitterSB: FittersSandboxService,
    private readonly picturesViewerService: PicturesViewerService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly insightService: InsightService,
    private readonly authService: AuthService,
    public featureFlippingManager: FeatureFlippingManagerService,
    private readonly snackbarService: SnackbarService,
    private readonly translator: TranslatorService,
    private readonly loadingService: LoadingService,
    private readonly appointmentSandbox: AppointmentsSandboxService,
    private readonly dialog: DialogService,
    private readonly overlayService: AdaptableOverlayService,
    private readonly router: Router,
    private readonly declarationConditions: DeclarationConditions
  ) {
    this.insightService.trackPageView({
      name: 'Declaration end page',
    });

    this.initNavigation();
  }

  initNavigation() {
    this.sandbox.setCurrentStep(InsurerDeclarationSteps.summaryStep, true);
  }

  ngOnInit(): void {
    this.snackbarService.setFocusManagement(true, {
      documentQuery: 'sc-declaration-end',
    });
    window.scrollTo(0, 0);

    this.authService.userIsFitterB2B$().subscribe({
      next: (isFitterB2B) => (this.isFitterB2B = isFitterB2B),
    });

    this.dossier$
      .pipe(
        filter((dossier) => !!dossier),
        take(1),
        tap((dossier) => {
          this.currentDossier = dossier;

          if (
            this.featureFlippingManager.isFeatureActivated(
              'b2b-allow-document-management-insurer'
            )
          ) {
            this.pictures = this.dossierSB.getClaimPicturesFromDossierDocuments(
              this.currentDossier?.claim?.documents
            );
          }

          if (this.currentDossier.claim.car.parts) {
            this.carParts = this.currentDossier.claim.car.parts;
            this.loadingCarParts = false;
          }

          this.callNumbers = {
            fromNumber: this.fitter?.phone,
            toNumber: dossier.customer?.phoneNumber?.number,
          };

          this.changeDetectorRef.markForCheck();
        }),
        switchMap(() => {
          const usedFitterId =
            this.currentDossier?.appointment?.fitterId ||
            this.currentDossier?.requestAppointment?.fitterId;

          const usedBrandId =
            this.currentDossier?.appointment?.brandId ||
            this.currentDossier?.requestAppointment?.brandId;

          if (!usedFitterId || !usedBrandId) {
            return EMPTY;
          }

          return this.fitterSB.fetchFitter({
            id: usedFitterId,
            brandId: usedBrandId,
          });
        })
      )
      .subscribe({
        next: (fitter) => {
          this.fitter = fitter;
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  ngOnDestroy(): void {
    this.snackbarService.setFocusManagement(false);
  }

  onShowPictures(data: PicturesViewerData): void {
    const usedPicture = data.pictures[data.index];
    const fileType = usedPicture?.metadata?.mimeType || usedPicture?.file?.type;
    if (withPreviewFileTypes.includes(fileType)) {
      this.picturesViewerService.show(data);
    } else if (usedPicture?.url) {
      window.open(usedPicture?.url as string, '_blank');
    }
  }

  onChangeAppointment() {
    // TODO look at impacts in the case of requestAppointment (function disabled for now)
    if (
      this.featureFlippingManager.isFeatureActivated(
        'can-do-appointment-modification-insurer'
      ) &&
      this.featureFlippingManager.isBrandFeatureActivated(
        this.fitter.brandId,
        'can-do-appointment-modification-fitter'
      )
    ) {
      if (
        this.declarationConditions.hasRequestedAppointment(this.currentDossier)
      ) {
        this.router
          .navigate([
            `/declaration/fitter/${this.currentDossier?.requestAppointment?.brandId}/${this.currentDossier?.requestAppointment?.fitterId}`,
          ])
          .catch((error) => console.error(error));
      } else {
        this.dossierSB
          .patchAppointment({
            ...this.currentDossier.appointment,
            status: 'MODIFIED',
          })
          .subscribe({
            next: () => {
              this.router
                .navigate([
                  `/declaration/fitter/${this.currentDossier.appointment.brandId}/${this.currentDossier.appointment.fitterId}`,
                ])
                .catch((error) => console.error(error));
            },
          });
      }
    }
  }

  onCancelAppointment() {
    if (
      this.featureFlippingManager.isFeatureActivated(
        'can-do-appointment-cancel-insurer'
      ) &&
      this.featureFlippingManager.isBrandFeatureActivated(
        this.fitter?.brandId,
        'can-do-appointment-cancel-fitter'
      )
    ) {
      this.overlayService
        .showOverlay({
          type: 'cancel-appointment',
          input: null,
        })
        .pipe(
          filter((overlayOutput) => overlayOutput.type === 'success'),
          tap(() => this.loadingService.setLoading(true)),
          switchMap((overlayOutput) =>
            this.appointmentSandbox.cancelAppointment({
              dossierId: this.currentDossier.id,
              comment:
                overlayOutput.type === 'success'
                  ? overlayOutput.data.comment
                  : '',
            })
          ),
          take(1)
        )
        .subscribe({
          next: (payload) => {
            this.snackbarService.showNotification(
              this.translator.getInstantTranslation(
                'DeclarationEndInformation.snackbarCancelTitle'
              ),
              this.translator.getInstantTranslation(
                'DeclarationEndInformation.snackbarCancelBody'
              ),
              4
            );
            this.dossierSB.patchDossier(payload.dossier);
            this.loadingService.setLoading(false);
          },
          error: (error) => {
            this.loadingService.setLoading(false);
            this.onCancelError(error);
          },
        });
    }
  }

  notifyCustomer() {
    this.loadingService.setLoading(true);
    this.dossierSB.notifyCustomer(this.currentDossier?.id)?.subscribe({
      next: () => {
        this.loadingService.setLoading(false);

        this.snackbarService.showNotification(
          this.translator.getInstantTranslation(
            'DeclarationEndInformation.snackbarNotifyTitle'
          ),
          this.translator.getInstantTranslation(
            'DeclarationEndInformation.snackbarNotifyBody'
          ),
          4
        );
      },
    });
  }

  validateRequest() {
    this.loadingService.setLoading(true);
    this.dossierSB.dossier$
      .pipe(
        take(1),
        switchMap((dossier) => {
          if (!dossier.requestAppointment) {
            this.loadingService.setLoading(false);
            return EMPTY;
          }

          return this.dossierSB.patchDossier({
            ...dossier,
            requestAppointment: {
              ...dossier.requestAppointment,
              isValidatedByFitter: true,
            },
          });
        }),
        switchMap(() => this.dossierSB.updateDossier())
      )
      .subscribe({
        next: (dossier) => {
          this.loadingService.setLoading(false);
          this.currentDossier = dossier;

          this.snackbarService.showNotification(
            this.translator.getInstantTranslation(
              'DeclarationEndInformation.dossierIsValidatedSnackbarTitle'
            ),
            this.translator.getInstantTranslation(
              'DeclarationEndInformation.dossierIsValidatedSnackbarMessage'
            ),
            4
          );
        },
        error: () => {
          this.loadingService.setLoading(false);
        },
      });
  }

  getLicensePlateValue() {
    return this.currentDossier?.claim?.car?.licensePlateNumber
      ? this.currentDossier?.claim?.car?.licensePlateNumber?.toLocaleUpperCase()
      : this.translator?.getInstantTranslation(
          'DeclarationEnd.vehicleNotFound'
        );
  }

  onCancelError(error: any) {
    const errorMessageContent = error?.error?.message
      ? this.translator.getInstantTranslation(error?.error?.message)
      : this.translator.getInstantTranslation('Booking.errors.generic');

    this.dialog.showError(
      this.translator.getInstantTranslation(
        'InsurerDeclarationIntervention.reservation'
      ),
      errorMessageContent
    );
  }
}
